import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { PageHeader, Loader } from "@/components/common/Bits";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  QrCode,
  Copy,
  Store,
  Receipt,
  ShieldCheck,
  CreditCard,
  CheckCircle2,
  RefreshCw,
  CreditCard as Card,
  Headphones,
} from "lucide-react";
import { toast } from "sonner";

const SECTIONS = [
  { key: "profile", label: "Restaurant Profile", icon: Store },
  { key: "approval", label: "Order Approval", icon: CheckCircle2 },
  { key: "payments", label: "Payments & GST", icon: CreditCard },
  { key: "qr", label: "QR Management", icon: QrCode },
  { key: "printer", label: "Printer", icon: Receipt },
  { key: "roles", label: "Roles & Permissions", icon: ShieldCheck },
  { key: "subscription", label: "Subscription", icon: Card },
  { key: "support", label: "Support", icon: Headphones },
];

const PERMISSIONS = [
  ["dashboard", "Dashboard"],
  ["pos", "POS / Billing"],
  ["tables", "Tables"],
  ["orders", "Orders"],
  ["approvals", "Order Approvals"],
  ["kitchen", "Kitchen / KDS"],
  ["menu", "Menu"],
  ["inventory", "Inventory"],
  ["customers", "Customers"],
  ["staff", "Staff"],
  ["reports", "Reports"],
  ["reservations", "Reservations"],
  ["expenses", "Expenses"],
  ["settings", "Settings"],
];

const ROLE_LABELS = {
  manager: "Manager",
  supervisor: "Supervisor",
  cashier: "Cashier",
  captain: "Captain",
  kitchen: "Kitchen",
  inventory_manager: "Inventory Manager",
};

const CATEGORY_LABELS = {
  technical_issue: "Technical Issue",
  payment_billing: "Payment / Billing",
  subscription: "Subscription",
  login_account: "Login / Account",
  pos_billing: "POS / Billing",
  menu_inventory: "Menu / Inventory",
  orders_kds: "Orders / KDS",
  other: "Other",
};

export default function Settings() {
  const { restaurant, branches, branchId } = useApp();
  const { user } = useAuth();
  const [tab, setTab] = useState("profile");
  const navigate = useNavigate();
  const { refreshRestaurantData } = useApp();
  const [selectedBranchId, setSelectedBranchId] = useState(
    branchId === "all" ? branches[0]?.id || "" : branchId || "",
  );
  const [branchForm, setBranchForm] = useState({
    name: "",
    address: "",
    phone: "",
    gstin: "",
    receipt_footer: "",
  });

  const [restaurantForm, setRestaurantForm] = useState({
    name: "",
    tagline: "",
  });
  const [profileErrors, setProfileErrors] = useState({});

  const [addingBranch, setAddingBranch] = useState(false);
  const [savingBranch, setSavingBranch] = useState(false);
  const [settings, setSettings] = useState(null);
  const [branchList, setBranchList] = useState([]);
  const [rest, setRest] = useState(null);
  const [plans, setPlans] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [hasStaff, setHasStaff] = useState(false);
  const [tables, setTables] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const activeBranch = branchId === "all" ? branches[0]?.id : branchId;

  const isStarterPlan =
    (restaurant?.plan || restaurant?.subscription?.plan || "").toLowerCase() ===
    "starter";

  const visibleSections = isStarterPlan
    ? SECTIONS.filter((section) =>
        ["profile", "payments", "printer", "support"].includes(section.key),
      )
    : SECTIONS;

  const [gstForm, setGstForm] = useState({
    enabled: false,
    rate: 0,
  });

  const [upiForm, setUpiForm] = useState({
    upi_id: "",
  });

  const [razorpayForm, setRazorpayForm] = useState({
    enabled: false,
    key_id: "",
    mode: "test",
    key_secret: "",
  });

  const [discountForm, setDiscountForm] = useState({
    Cash: 0,
    UPI: 0,
    Razorpay: 0,
  });

  const [savingPaymentBlock, setSavingPaymentBlock] = useState("");

  useEffect(() => {
    if (!selectedBranchId && branchList.length > 0) {
      setSelectedBranchId(
        branchId === "all"
          ? branchList[0]?.id || ""
          : branchId || branchList[0]?.id || "",
      );
    }
  }, [branchId, branchList, selectedBranchId]);

  const [supportTickets, setSupportTickets] = useState([]);
  const [supportLoading, setSupportLoading] = useState(false);
  const [supportSubmitting, setSupportSubmitting] = useState(false);

  const [supportForm, setSupportForm] = useState({
    category: "technical_issue",
    priority: "medium",
    subject: "",
    description: "",
  });

  const loadSupportTickets = async () => {
    try {
      setSupportLoading(true);

      const response = await api.mySupportTickets();

      const data = Array.isArray(response.data) ? response.data : [];

      setSupportTickets(data);
    } catch (e) {
      console.error("Failed to load support tickets", e);

      toast.error(e.response?.data?.detail || "Failed to load support tickets");

      setSupportTickets([]);
    } finally {
      setSupportLoading(false);
    }
  };

  const createSupportTicket = async () => {
    const subject = supportForm.subject.trim();

    const description = supportForm.description.trim();

    if (!subject) {
      toast.error("Subject is required");
      return;
    }

    if (!description) {
      toast.error("Please describe your problem");
      return;
    }

    try {
      setSupportSubmitting(true);

      await api.createSupportTicket({
        category: supportForm.category,
        priority: supportForm.priority,
        subject,
        description,
      });

      toast.success("Support ticket created successfully");

      setSupportForm({
        category: "technical_issue",
        priority: "medium",
        subject: "",
        description: "",
      });

      await loadSupportTickets();
    } catch (e) {
      console.error("Failed to create support ticket", e);

      toast.error(
        e.response?.data?.detail || "Failed to create support ticket",
      );
    } finally {
      setSupportSubmitting(false);
    }
  };

  useEffect(() => {
    const branch = branchList.find((b) => b.id === selectedBranchId);

    setBranchForm({
      name: branch?.name || "",
      address: branch?.address || "",
      phone: branch?.phone || "",
      gstin: branch?.gstin || "",
      receipt_footer: branch?.receipt_footer || "",
    });

    setProfileErrors({});
  }, [selectedBranchId, branchList]);

  useEffect(() => {
    const loadStaff = async () => {
      if (!activeBranch) {
        setStaffList([]);
        return;
      }

      setLoadingStaff(true);

      try {
        const res = await api.staff(activeBranch);

        const data = res.data;

        setStaffList(
          Array.isArray(data)
            ? data
            : Array.isArray(data?.staff)
              ? data.staff
              : [],
        );
      } catch (e) {
        console.error("Failed to load staff", e);
        setStaffList([]);
      } finally {
        setLoadingStaff(false);
      }
    };

    loadStaff();
  }, [activeBranch]);

  const load = async () => {
    try {
      const settingsRes = await api.settings();

      setSettings(settingsRes.data.settings);
      setRest(settingsRes.data.restaurant);

      const loadedRestaurant = settingsRes.data.restaurant || {};
      setRestaurantForm({
        name: loadedRestaurant.name || "",
        tagline: loadedRestaurant.tagline || "",
      });

      const loadedSettings = settingsRes.data.settings || {};

      setGstForm({
        enabled: Boolean(loadedSettings.gst_enabled),
        rate: loadedSettings.gst_rate ?? 0,
      });

      setUpiForm({
        upi_id: loadedSettings.upi_id || "",
      });

      setRazorpayForm({
        enabled: Boolean(settingsRes.data.settings?.razorpay_enabled),
        key_id: settingsRes.data.settings?.razorpay_key_id || "",
        mode: settingsRes.data.settings?.razorpay_mode || "test",
        key_secret: "",
      });

      setDiscountForm({
        Cash: loadedSettings.payment_discounts?.Cash ?? 0,
        UPI: loadedSettings.payment_discounts?.UPI ?? 0,
        Razorpay: loadedSettings.payment_discounts?.Razorpay ?? 0,
      });

      try {
        const branchesRes = await api.branches();
        const data = branchesRes.data;

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.branches)
            ? data.branches
            : [];

        setBranchList(list);

        setRolePermissions(settingsRes.data.settings?.role_permissions || {});

        // Roles & Permissions is shown only when staff accounts exist.
        // The backend calculates this from the authenticated restaurant, so
        // another tenant can never affect this UI state.
        setHasStaff(Boolean(settingsRes.data?.has_staff));
      } catch (e) {
        console.error("Failed to load branches", e);
        setBranchList([]);
        setHasStaff(false);
      }
    } catch (e) {
      console.error("Failed to load settings", e);
      toast.error("Failed to load settings");
    }
  };

  useEffect(() => {
    load();
    api
      .plans()
      .then((r) => setPlans(r.data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "support") {
      loadSupportTickets();
    }
  }, [tab]);

  useEffect(() => {
    if (activeBranch)
      api
        .tables(activeBranch)
        .then((r) => setTables(r.data))
        .catch(() => {});
  }, [activeBranch]);

  const save = async (patch) => {
    try {
      const response = await api.updateSettings(patch);

      setSettings(response.data.settings);
      setRest(response.data.restaurant);

      toast.success("Settings saved");
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || "Failed to save settings");
    }
  };

  const toggleRolePermission = (role, permission) => {
    setRolePermissions((current) => {
      const existing = current[role] || [];

      const updated = existing.includes(permission)
        ? existing.filter((p) => p !== permission)
        : [...existing, permission];

      return {
        ...current,
        [role]: updated,
      };
    });
  };

  const saveRolePermissions = async () => {
    setSavingPermissions(true);

    try {
      await api.updateRolePermissions(rolePermissions);
      toast.success("Permissions saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save permissions");
    } finally {
      setSavingPermissions(false);
    }
  };

  const saveBranch = async () => {
    if (!selectedBranchId) return;

    if (!branchForm.name.trim()) {
      toast.error("Branch name is required");
      return;
    }

    setSavingBranch(true);

    try {
      await api.updateBranch(selectedBranchId, branchForm);
      toast.success("Branch saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save branch");
    } finally {
      setSavingBranch(false);
    }
  };

  const validateProfile = () => {
    const errors = {};

    const branchName = branchForm.name.trim();
    const address = branchForm.address.trim();
    const phone = branchForm.phone.trim();
    const gstin = branchForm.gstin.trim().toUpperCase();
    const restaurantName = restaurantForm.name.trim();
    const tagline = restaurantForm.tagline.trim();

    // Branch Name
    if (!branchName) {
      errors.name = "Branch name is required";
    } else if (branchName.length < 2) {
      errors.name = "Branch name must be at least 2 characters";
    } else if (branchName.length > 100) {
      errors.name = "Branch name must not exceed 100 characters";
    }

    // Address
    if (!address) {
      errors.address = "Address is required";
    } else if (address.length < 5) {
      errors.address = "Please enter a valid address";
    } else if (address.length > 250) {
      errors.address = "Address must not exceed 250 characters";
    }

    // Phone
    if (!phone) {
      errors.phone = "Phone number is required";
    } else if (!/^[6-9]\d{9}$/.test(phone)) {
      errors.phone = "Enter a valid 10-digit mobile number";
    }

    // GSTIN
    if (gstin && !/^[0-9A-Z]{15}$/.test(gstin)) {
      errors.gstin = "GSTIN must be exactly 15 characters";
    }

    // Restaurant Name
    if (!restaurantName) {
      errors.restaurantName = "Restaurant name is required";
    } else if (restaurantName.length < 2) {
      errors.restaurantName = "Restaurant name must be at least 2 characters";
    } else if (restaurantName.length > 100) {
      errors.restaurantName = "Restaurant name must not exceed 100 characters";
    }

    // Tagline
    if (tagline.length > 150) {
      errors.tagline = "Tagline must not exceed 150 characters";
    }

    setProfileErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const saveProfileChanges = async () => {
    if (!selectedBranchId) {
      toast.error("Please select a branch");
      return;
    }

    if (!validateProfile()) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    setSavingBranch(true);

    try {
      // Save branch details
      await api.updateBranch(selectedBranchId, {
        name: branchForm.name.trim(),
        address: branchForm.address.trim(),
        phone: branchForm.phone.trim(),
        gstin: branchForm.gstin.trim().toUpperCase(),
        receipt_footer: branchForm.receipt_footer.trim(),
      });

      // Save restaurant details through settings API
      const response = await api.updateSettings({
        restaurant_name: restaurantForm.name.trim(),
        tagline: restaurantForm.tagline.trim(),
      });

      const updatedSettings = response.data?.settings || response.data || {};

      setSettings(updatedSettings);
      if (response.data?.restaurant) {
        setRest(response.data.restaurant);
      }

      setRestaurantForm({
        name: restaurantForm.name.trim(),
        tagline: restaurantForm.tagline.trim(),
      });

      setProfileErrors({});

      toast.success("Profile settings saved");

      await load();
      await refreshRestaurantData();
    } catch (e) {
      console.error("Failed to save profile", e);

      toast.error(
        e.response?.data?.detail || "Failed to save profile settings",
      );
    } finally {
      setSavingBranch(false);
    }
  };

  const saveGst = async () => {
    const rawRate = String(gstForm.rate ?? "").trim();

    // Empty rate is allowed only when GST is disabled.
    if (!gstForm.enabled && rawRate === "") {
      setSavingPaymentBlock("gst");

      try {
        await api.updateSettings({
          gst_enabled: false,
          gst_rate: 0,
        });

        setSettings((current) => ({
          ...current,
          gst_enabled: false,
          gst_rate: 0,
        }));

        setGstForm({
          enabled: false,
          rate: 0,
        });

        toast.success("GST disabled");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to save GST settings");
      } finally {
        setSavingPaymentBlock("");
      }

      return;
    }

    const rate = Number(rawRate);

    // GST enabled → valid positive rate required
    if (gstForm.enabled) {
      if (!Number.isFinite(rate) || rate < 0 || rate > 28) {
        toast.error("GST rate must be between 0% and 28%");
        return;
      }
    }

    // GST disabled → rate must still be valid
    if (!gstForm.enabled) {
      if (!Number.isFinite(rate) || rate < 0 || rate > 28) {
        toast.error("GST rate must be between 0% and 28%");
        return;
      }
    }

    setSavingPaymentBlock("gst");

    try {
      await api.updateSettings({
        gst_enabled: gstForm.enabled,
        gst_rate: rate,
      });

      setSettings((current) => ({
        ...current,
        gst_enabled: gstForm.enabled,
        gst_rate: rate,
      }));

      setGstForm({
        enabled: gstForm.enabled,
        rate,
      });

      toast.success("GST settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save GST settings");
    } finally {
      setSavingPaymentBlock("");
    }
  };
  const saveUpi = async () => {
    const upi = upiForm.upi_id.trim();

    if (upi && !/^[\w.-]+@[\w.-]+$/.test(upi)) {
      toast.error("Please enter a valid UPI ID");
      return;
    }

    setSavingPaymentBlock("upi");

    try {
      await api.updateSettings({
        upi_id: upi,
      });

      setSettings((current) => ({
        ...current,
        upi_id: upi,
      }));

      toast.success("UPI settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save UPI settings");
    } finally {
      setSavingPaymentBlock("");
    }
  };

  const saveRazorpay = async () => {
    // Razorpay disabled
    // Only save the disabled state.
    if (!razorpayForm.enabled) {
      setSavingPaymentBlock("razorpay");

      try {
        await api.updateSettings({
          razorpay_enabled: false,
        });

        setSettings((current) => ({
          ...current,
          razorpay_enabled: false,
        }));

        toast.success("Razorpay disabled");
      } catch (e) {
        toast.error(
          e.response?.data?.detail || "Failed to save Razorpay settings",
        );
      } finally {
        setSavingPaymentBlock(null);
      }

      return;
    }

    // Razorpay enabled → Key ID is mandatory
    const keyId = razorpayForm.key_id.trim();

    if (!keyId) {
      toast.error("Razorpay Key ID is required when Razorpay is enabled");
      return;
    }

    // If there is no existing secret, a new secret is required.
    const hasExistingSecret = Boolean(settings?.razorpay_key_secret_configured);

    if (!razorpayForm.key_secret.trim() && !hasExistingSecret) {
      toast.error("Razorpay Key Secret is required when Razorpay is enabled");
      return;
    }

    setSavingPaymentBlock("razorpay");

    try {
      const payload = {
        razorpay_enabled: true,
        razorpay_key_id: keyId,
        razorpay_mode: razorpayForm.mode,
      };

      // Only send secret when user entered a new one.
      if (razorpayForm.key_secret.trim()) {
        payload.razorpay_key_secret = razorpayForm.key_secret.trim();
      }

      await api.updateSettings(payload);

      setSettings((current) => ({
        ...current,
        razorpay_enabled: true,
        razorpay_key_id: keyId,
        razorpay_mode: razorpayForm.mode,
        razorpay_key_secret_configured:
          Boolean(razorpayForm.key_secret.trim()) ||
          Boolean(current?.razorpay_key_secret_configured),
      }));

      setRazorpayForm((current) => ({
        ...current,
        key_secret: "",
      }));

      toast.success("Razorpay settings saved");
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Failed to save Razorpay settings",
      );
    } finally {
      setSavingPaymentBlock(null);
    }
  };
  const saveDiscounts = async () => {
    const cash = Number(discountForm.Cash);
    const upi = Number(discountForm.UPI);
    const razorpay = Number(discountForm.Razorpay);

    const values = [cash, upi, razorpay];

    if (values.some((v) => !Number.isFinite(v) || v < 0 || v > 100)) {
      toast.error("Discount must be between 0% and 100%");
      return;
    }

    setSavingPaymentBlock("discounts");

    try {
      const payment_discounts = {
        Cash: cash,
        UPI: upi,
        Razorpay: razorpay,
      };

      await api.updateSettings({
        payment_discounts,
      });

      setSettings((current) => ({
        ...current,
        payment_discounts,
      }));

      toast.success("Payment discounts saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save discounts");
    } finally {
      setSavingPaymentBlock("");
    }
  };

  const createNewBranch = async () => {
    if (!branchForm.name.trim()) {
      toast.error("Branch name is required");
      return;
    }

    setSavingBranch(true);

    try {
      const r = await api.createBranch(branchForm);

      toast.success("Branch created");

      await load();

      setAddingBranch(false);
      setSelectedBranchId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create branch");
    } finally {
      setSavingBranch(false);
    }
  };
  const regen = async (t) => {
    try {
      const r = await api.regenerateQr(t.id);
      setTables((arr) => arr.map((x) => (x.id === t.id ? r.data : x)));
      toast.success(`QR regenerated for ${t.name}`);
    } catch (e) {
      toast.error("Failed");
    }
  };

  const qrLink = (t) =>
    `${window.location.origin}/qr?branch_id=${t.branch_id}&table=${t.name}&t=${t.qr_token || ""}`;

  if (!settings)
    return (
      <div className="p-6">
        <Loader label="Loading settings" />
      </div>
    );

  const downloadPaymentQr = async () => {
    if (!settings?.upi_id) {
      toast.error("UPI ID not configured");
      return;
    }

    try {
      const selectedBranch = branchList.find((b) => b.id === selectedBranchId);

      const qrData = `upi://pay?pa=${settings.upi_id}&pn=${encodeURIComponent(
        `${rest?.name || "Restaurant"} - ${selectedBranch?.name || "Branch"}`,
      )}`;

      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(
        qrData,
      )}`;

      const response = await fetch(qrUrl);
      const blob = await response.blob();

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `${rest?.name || "restaurant"}-${selectedBranch?.name || "branch"}-payment-qr.png`;

      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error("Failed to download QR");
    }
  };
  return (
    <div className="p-4 sm:p-6 animate-fade-up">
      <PageHeader title="Settings" subtitle="Configure your restaurant" />
      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
        <div className="space-y-1">
          {visibleSections.map((s) => (
            <button
              key={s.key}
              onClick={() => setTab(s.key)}
              data-testid={`settings-${s.key}`}
              className={cn(
                "w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors",
                tab === s.key
                  ? "bg-primary text-white shadow-[0_4px_12px_rgba(234,88,12,0.25)]"
                  : "hover:bg-secondary text-foreground",
              )}
            >
              <s.icon size={17} /> {s.label}
            </button>
          ))}
        </div>

        <div className="rounded-2xl bg-card border border-border p-6">
          {tab === "profile" && (
            <div className="space-y-5 max-w-lg">
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    Branch
                  </Label>

                  <select
                    value={addingBranch ? "" : selectedBranchId}
                    onChange={(e) => {
                      setAddingBranch(false);
                      setSelectedBranchId(e.target.value);
                    }}
                    className="mt-1.5 h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                    data-testid="settings-branch-select"
                  >
                    <option value="" disabled>
                      Select branch
                    </option>

                    {branchList.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                <Field
                  label="Branch Name"
                  value={branchForm.name}
                  onChange={(v) =>
                    setBranchForm((f) => ({
                      ...f,
                      name: v,
                    }))
                  }
                  error={profileErrors.name}
                  required
                  testid="branch-name"
                />

                <Field
                  label="Address"
                  value={branchForm.address}
                  onChange={(v) =>
                    setBranchForm((f) => ({
                      ...f,
                      address: v,
                    }))
                  }
                  error={profileErrors.address}
                  required
                  testid="branch-address"
                />

                <Field
                  label="Phone"
                  value={branchForm.phone}
                  onChange={(v) =>
                    setBranchForm((f) => ({
                      ...f,
                      phone: v.replace(/\D/g, "").slice(0, 10),
                    }))
                  }
                  error={profileErrors.phone}
                  required
                  testid="branch-phone"
                />
                {!isStarterPlan && (
                  <Field
                    label="GSTIN"
                    value={branchForm.gstin}
                    onChange={(v) =>
                      setBranchForm((f) => ({
                        ...f,
                        gstin: v
                          .toUpperCase()
                          .replace(/[^A-Z0-9]/g, "")
                          .slice(0, 15),
                      }))
                    }
                    error={profileErrors.gstin}
                    testid="branch-gstin"
                  />
                )}
                <Field
                  label="Receipt Footer"
                  value={branchForm.receipt_footer}
                  onChange={(v) =>
                    setBranchForm((f) => ({ ...f, receipt_footer: v }))
                  }
                  testid="branch-receipt-footer"
                />
              </div>

              <div className="border-t border-border pt-4">
                <div className="space-y-4">
                  <Field
                    label="Restaurant Name"
                    value={restaurantForm.name}
                    onChange={(v) =>
                      setRestaurantForm((f) => ({
                        ...f,
                        name: v,
                      }))
                    }
                    error={profileErrors.restaurantName}
                    required
                    testid="restaurant-name"
                  />

                  <Field
                    label="Tagline"
                    value={restaurantForm.tagline}
                    onChange={(v) =>
                      setRestaurantForm((f) => ({
                        ...f,
                        tagline: v.slice(0, 150),
                      }))
                    }
                    error={profileErrors.tagline}
                    testid="restaurant-tagline"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    type="button"
                    className="h-11 rounded-xl font-semibold"
                    disabled={savingBranch}
                    onClick={saveProfileChanges}
                  >
                    {savingBranch ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {tab === "approval" && (
            <>
              <div className="max-w-lg space-y-4">
                <h3 className="font-display font-semibold">
                  Order Approval Workflow
                </h3>
                <p className="text-sm text-muted-foreground">
                  Control whether orders need authorization before reaching the
                  kitchen.
                </p>
                {[
                  [
                    "approval_required",
                    "Approval Required (Default)",
                    "Every order — QR, captain, phone or POS — must be approved by an authorized user before a KOT is generated.",
                  ],
                  [
                    "role_based",
                    "Role-Based Direct",
                    "Owners, managers and supervisors send orders straight to the kitchen. Everyone else still needs approval.",
                  ],
                ].map(([val, title, desc]) => (
                  <button
                    key={val}
                    onClick={() => save({ approval_mode: val })}
                    data-testid={`approval-mode-${val}`}
                    className={cn(
                      "w-full text-left rounded-2xl border p-4 transition-colors",
                      settings.approval_mode === val
                        ? "border-primary bg-accent"
                        : "border-border hover:bg-secondary",
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{title}</span>
                      {settings.approval_mode === val && (
                        <CheckCircle2 size={18} className="text-primary" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{desc}</p>
                  </button>
                ))}
              </div>
              <div className="border-t border-border pt-2 mt-6">
                <h3 className="font-display font-semibold text-lg">
                  Order Availability
                </h3>

                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Choose which order types this restaurant accepts.
                </p>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => save({ availability_mode: "hybrid" })}
                    className={cn(
                      "w-full text-left rounded-2xl border p-4 transition-colors",
                      settings.availability_mode === "hybrid"
                        ? "border-primary bg-accent"
                        : "border-border hover:bg-secondary",
                    )}
                  >
                    <div className="font-semibold">Delivery Available</div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Customers can place delivery orders when the restaurant
                      supports delivery.
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => save({ availability_mode: "dine_in_only" })}
                    className={cn(
                      "w-full text-left rounded-2xl border p-4 transition-colors",
                      settings.availability_mode === "dine_in_only"
                        ? "border-primary bg-accent"
                        : "border-border hover:bg-secondary",
                    )}
                  >
                    <div className="font-semibold">Delivery Disabled</div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Hide delivery ordering for this restaurant.
                    </p>
                  </button>
                </div>
              </div>
            </>
          )}
          {tab === "payments" && (
            <div className="max-w-2xl space-y-6">
              {/* ================= GST ================= */}
              <div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display font-semibold text-lg">
                      GST & Tax
                    </h3>

                    <p className="text-sm text-muted-foreground mt-1">
                      Configure GST used for bills and receipts.
                    </p>
                  </div>

                  <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <input
                      type="checkbox"
                      checked={gstForm.enabled}
                      onChange={(e) =>
                        setGstForm((current) => ({
                          ...current,
                          enabled: e.target.checked,
                        }))
                      }
                      className="h-5 w-5"
                    />

                    <span>{gstForm.enabled ? "Enabled" : "Disabled"}</span>
                  </label>
                </div>

                <div className="mt-4">
                  <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                    GST Rate (%)
                  </Label>

                  <Input
                    type="number"
                    min="0"
                    max="28"
                    step="0.01"
                    value={gstForm.enabled ? gstForm.rate : 0}
                    disabled={!gstForm.enabled}
                    onChange={(e) => {
                      const value = e.target.value;

                      // Allow empty while editing.
                      if (value === "") {
                        setGstForm((current) => ({
                          ...current,
                          rate: "",
                        }));
                        return;
                      }

                      const numericValue = Number(value);

                      // Do not allow negative values.
                      if (numericValue < 0) {
                        return;
                      }

                      // Do not allow values above 28.
                      if (numericValue > 28) {
                        return;
                      }

                      setGstForm((current) => ({
                        ...current,
                        rate: value,
                      }));
                    }}
                    className="h-11 rounded-xl mt-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="gst-rate"
                    placeholder="Enter GST rate"
                  />

                  <p className="text-xs text-muted-foreground mt-1.5">
                    {gstForm.enabled
                      ? "GST will be applied to applicable bills."
                      : "GST is disabled. No GST will be applied to bills."}
                  </p>
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    type="button"
                    className="h-10 rounded-xl"
                    disabled={savingPaymentBlock === "gst"}
                    onClick={saveGst}
                  >
                    {savingPaymentBlock === "gst" ? "Saving..." : "Save GST"}
                  </Button>
                </div>
              </div>

              {/* ================= UPI ================= */}
              <div className="border-t border-border pt-6">
                <div>
                  <h3 className="font-display font-semibold text-lg">
                    UPI / QR Payment
                  </h3>

                  <p className="text-sm text-muted-foreground mt-1 mb-4">
                    Configure the UPI account used by the restaurant to receive
                    payments.
                  </p>
                </div>

                <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                  Restaurant UPI ID
                </Label>

                <Input
                  value={upiForm.upi_id}
                  onChange={(e) =>
                    setUpiForm({
                      upi_id: e.target.value,
                    })
                  }
                  className="h-11 rounded-xl mt-1.5"
                  data-testid="upi-id"
                  placeholder="example@upi"
                />

                <div className="flex justify-end mt-4">
                  <Button
                    type="button"
                    className="h-10 rounded-xl"
                    disabled={savingPaymentBlock === "upi"}
                    onClick={saveUpi}
                  >
                    {savingPaymentBlock === "upi" ? "Saving..." : "Save UPI"}
                  </Button>
                </div>

                <div className="mt-5 rounded-2xl border border-border p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold">Payment QR</p>

                      <p className="text-xs text-muted-foreground mt-1">
                        Customers can scan this QR to pay the restaurant.
                      </p>
                    </div>

                    <QrCode size={24} className="text-primary" />
                  </div>

                  <div className="mt-4 flex items-center gap-4">
                    {upiForm.upi_id.trim() &&
                    /^[\w.-]+@[\w.-]+$/.test(upiForm.upi_id.trim()) ? (
                      <img
                        alt="Restaurant payment QR"
                        className="h-32 w-32 rounded-xl border border-border"
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
                          `upi://pay?pa=${upiForm.upi_id.trim()}&pn=${
                            rest?.name || "Restaurant"
                          }`,
                        )}`}
                      />
                    ) : (
                      <div className="h-32 w-32 rounded-xl border border-dashed border-border grid place-items-center text-xs text-muted-foreground text-center p-3">
                        Enter a valid UPI ID to generate QR
                      </div>
                    )}

                    <div className="text-sm text-muted-foreground">
                      <p>
                        <span className="font-semibold text-foreground">
                          UPI ID:
                        </span>{" "}
                        {upiForm.upi_id || "Not configured"}
                      </p>

                      <p className="mt-2">
                        This QR is for restaurant payment. Table/order QR codes
                        remain separate.
                      </p>
                    </div>
                  </div>

                  {upiForm.upi_id.trim() &&
                    /^[\w.-]+@[\w.-]+$/.test(upiForm.upi_id.trim()) && (
                      <Button
                        type="button"
                        variant="outline"
                        className="h-10 rounded-xl mt-4"
                        onClick={downloadPaymentQr}
                      >
                        Download QR
                      </Button>
                    )}
                </div>
              </div>

              {/* ================= RAZORPAY ================= */}
              <div className="border-t border-border pt-6">
                <h3 className="font-display font-semibold text-lg">Razorpay</h3>

                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Configure online payments through Razorpay.
                </p>

                <div className="space-y-4">
                  {/* Enable / Disable - NO API CALL */}
                  <label className="flex items-center justify-between rounded-xl border border-border p-4 cursor-pointer">
                    <div>
                      <p className="font-semibold">Enable Razorpay</p>

                      <p className="text-xs text-muted-foreground mt-1">
                        Allow Razorpay as a payment method at POS.
                      </p>
                    </div>

                    <input
                      type="checkbox"
                      checked={razorpayForm.enabled}
                      onChange={(e) =>
                        setRazorpayForm((current) => ({
                          ...current,
                          enabled: e.target.checked,
                        }))
                      }
                      className="h-5 w-5"
                    />
                  </label>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        Razorpay Key ID
                      </Label>

                      <Input
                        value={razorpayForm.key_id}
                        onChange={(e) =>
                          setRazorpayForm((current) => ({
                            ...current,
                            key_id: e.target.value,
                          }))
                        }
                        disabled={!razorpayForm.enabled}
                        className="h-11 rounded-xl mt-1.5"
                        data-testid="razorpay-key-id"
                        placeholder="rzp_test_..."
                      />
                    </div>

                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        Mode
                      </Label>

                      <select
                        value={razorpayForm.mode}
                        onChange={(e) =>
                          setRazorpayForm((current) => ({
                            ...current,
                            mode: e.target.value,
                          }))
                        }
                        disabled={!razorpayForm.enabled}
                        className="mt-1.5 h-11 w-full rounded-xl border border-border bg-white px-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="test">Test</option>
                        <option value="live">Live</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Razorpay Key Secret
                    </Label>

                    <Input
                      type="password"
                      value={razorpayForm.key_secret}
                      onChange={(e) =>
                        setRazorpayForm((current) => ({
                          ...current,
                          key_secret: e.target.value,
                        }))
                      }
                      disabled={!razorpayForm.enabled}
                      placeholder="Paste your Razorpay Key Secret (write-only)"
                      className="h-11 rounded-xl mt-1.5"
                      data-testid="razorpay-key-secret"
                      autoComplete="new-password"
                    />

                    <p className="text-xs text-muted-foreground mt-1">
                      Leave blank if you do not want to replace the existing
                      secret.
                    </p>
                  </div>

                  <div className="rounded-xl bg-secondary p-3 text-xs text-muted-foreground">
                    <b className="text-foreground">Security:</b> Razorpay Secret
                    Key is stored server-side and should never be returned to
                    the browser.
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      className="h-10 rounded-xl"
                      disabled={savingPaymentBlock === "razorpay"}
                      onClick={saveRazorpay}
                    >
                      {savingPaymentBlock === "razorpay"
                        ? "Saving..."
                        : "Save Razorpay"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* ================= DISCOUNTS ================= */}
              <div className="border-t border-border pt-6">
                <h3 className="font-display font-semibold text-lg">
                  Payment-method discounts
                </h3>

                <p className="text-sm text-muted-foreground mt-1 mb-4">
                  Discounts are applied at checkout based on the selected
                  payment method.
                </p>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    ["Cash", "Cash Discount (%)"],
                    ["UPI", "UPI Discount (%)"],
                    ["Razorpay", "Razorpay Discount (%)"],
                  ].map(([key, label]) => (
                    <div key={key}>
                      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        {label}
                      </Label>

                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={discountForm[key]}
                        onChange={(e) =>
                          setDiscountForm((current) => ({
                            ...current,
                            [key]: e.target.value,
                          }))
                        }
                        className="h-11 rounded-xl mt-1.5"
                      />
                    </div>
                  ))}
                </div>

                <div className="flex justify-end mt-4">
                  <Button
                    type="button"
                    className="h-10 rounded-xl"
                    disabled={savingPaymentBlock === "discounts"}
                    onClick={saveDiscounts}
                  >
                    {savingPaymentBlock === "discounts"
                      ? "Saving..."
                      : "Save Discounts"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {tab === "qr" && (
            <div>
              <h3 className="font-display font-semibold mb-1">
                Table QR Codes
              </h3>

              <p className="text-sm text-muted-foreground mb-4">
                Each table has a unique QR. Print and place it on the table —
                guests scan to order.
              </p>

              {tables.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-border p-10 text-center">
                  <QrCode size={40} className="mx-auto text-muted-foreground" />

                  <p className="font-display font-semibold text-lg mt-4">
                    No tables found
                  </p>

                  <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
                    No tables have been created for this branch yet. Create a
                    table first to generate its QR code.
                  </p>

                  <Button
                    type="button"
                    className="h-10 rounded-xl mt-5"
                    onClick={() => navigate("/app/tables")}
                  >
                    Add Table
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {tables.map((t) => (
                    <div
                      key={t.id}
                      className="rounded-2xl border border-border p-3 text-center"
                      data-testid={`qr-table-${t.id}`}
                    >
                      <img
                        alt={t.name}
                        className="h-24 w-24 mx-auto"
                        src={`https://api.qrserver.com/v1/create-qr-code/?size=140x140&data=${encodeURIComponent(
                          qrLink(t),
                        )}`}
                      />

                      <p className="font-display font-bold mt-1">{t.name}</p>

                      <p className="text-[10px] text-muted-foreground">
                        {t.floor}
                      </p>

                      <div className="flex gap-1 mt-2">
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard.writeText(qrLink(t));
                            toast.success("Link copied");
                          }}
                          className="flex-1 rounded-lg bg-secondary py-1.5 text-xs font-semibold hover:bg-stone-200"
                          data-testid={`qr-copy-${t.id}`}
                        >
                          <Copy size={12} className="inline" />
                        </button>

                        <a
                          href={qrLink(t)}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 rounded-lg bg-secondary py-1.5 text-xs font-semibold hover:bg-stone-200 grid place-items-center"
                        >
                          Open
                        </a>

                        <button
                          type="button"
                          onClick={() => regen(t)}
                          className="flex-1 rounded-lg bg-secondary py-1.5 text-xs font-semibold hover:bg-stone-200"
                          data-testid={`qr-regen-${t.id}`}
                        >
                          <RefreshCw size={12} className="inline" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === "printer" && (
            <div className="max-w-2xl">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-display font-semibold text-lg">
                    Printer Setup
                  </h3>

                  <p className="text-sm text-muted-foreground mt-1">
                    Set up printing on your device.
                  </p>
                </div>

                <Receipt size={24} className="text-primary shrink-0" />
              </div>

              <div className="mt-5 space-y-4">
                {/* ================= ANDROID ================= */}
                <div className="rounded-2xl border border-border p-5">
                  <div>
                    <p className="font-semibold">Android</p>

                    <p className="text-sm text-muted-foreground mt-1">
                      Install the StayServe Printer Bridge app to connect your
                      Bluetooth thermal printer and print receipts.
                    </p>
                  </div>

                  <div className="flex justify-end mt-5">
                    <a
                      href="/downloads/stayserve-printer-bridge.apk"
                      download
                      className="inline-flex h-10 items-center justify-center rounded-xl bg-primary px-5 text-sm font-semibold text-white transition-colors hover:opacity-90"
                    >
                      Download Android APK
                    </a>
                  </div>
                </div>

                {/* ================= WINDOWS ================= */}
                <div className="rounded-2xl border border-border p-5">
                  <div>
                    <p className="font-semibold">Windows</p>

                    <p className="text-sm text-muted-foreground mt-1">
                      Install the StayServe Windows printer driver to enable
                      receipt printing from your Windows computer.
                    </p>
                  </div>

                  <div className="flex justify-end mt-5">
                    <a
                      href="/downloads/stayserve-windows-printer-driver.exe"
                      download
                      className="inline-flex h-10 items-center justify-center rounded-xl border border-border px-5 text-sm font-semibold text-foreground transition-colors hover:bg-secondary"
                    >
                      Download Windows Driver
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "roles" && (
            <div className="max-w-2xl">
              {!hasStaff ? (
                /* ---------------- Empty State ---------------- */
                <div className="rounded-2xl border border-dashed border-border p-8 text-center">
                  <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-secondary grid place-items-center">
                    <ShieldCheck size={22} className="text-muted-foreground" />
                  </div>

                  <h3 className="font-display font-semibold text-lg">
                    No staff members yet
                  </h3>

                  <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                    Create staff members first to configure role-based
                    permissions for your restaurant.
                  </p>

                  <Button
                    type="button"
                    className="h-10 rounded-xl mt-5"
                    onClick={() => {
                      window.location.href = "/app/staff";
                    }}
                  >
                    Add Staff
                  </Button>
                </div>
              ) : (
                /* ---------------- Staff Exists ---------------- */
                <>
                  <div className="flex items-center justify-between mb-5">
                    <div>
                      <h3 className="font-display font-semibold text-lg">
                        Roles & Permissions
                      </h3>

                      <p className="text-sm text-muted-foreground mt-1">
                        Owner can control what each staff role can access.
                      </p>
                    </div>

                    <Button
                      type="button"
                      className="h-10 rounded-xl"
                      disabled={savingPermissions}
                      onClick={saveRolePermissions}
                    >
                      {savingPermissions ? "Saving..." : "Save Permissions"}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {[
                      ...new Set(
                        staffList
                          .map((staff) => staff.role)
                          .filter((role) => ROLE_LABELS[role]),
                      ),
                    ].map((role) => {
                      const label = ROLE_LABELS[role];

                      return (
                        <div
                          key={role}
                          className="rounded-2xl border border-border p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <p className="font-semibold">{label}</p>

                              <p className="text-xs text-muted-foreground mt-1">
                                Select modules this role can access.
                              </p>
                            </div>

                            <span className="text-xs text-muted-foreground">
                              {(rolePermissions[role] || []).length} selected
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {PERMISSIONS.map(
                              ([permission, permissionLabel]) => {
                                const enabled = (
                                  rolePermissions[role] || []
                                ).includes(permission);

                                return (
                                  <label
                                    key={permission}
                                    className={cn(
                                      "flex items-center justify-between rounded-xl border px-3 py-2.5 cursor-pointer transition-colors",
                                      enabled
                                        ? "border-primary bg-accent"
                                        : "border-border hover:bg-secondary",
                                    )}
                                  >
                                    <span className="text-sm">
                                      {permissionLabel}
                                    </span>

                                    <input
                                      type="checkbox"
                                      checked={enabled}
                                      onChange={() =>
                                        toggleRolePermission(role, permission)
                                      }
                                      className="h-4 w-4"
                                    />
                                  </label>
                                );
                              },
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 rounded-xl bg-secondary p-3 text-xs text-muted-foreground">
                    <b className="text-foreground">Owner:</b> Full access is
                    always enabled and cannot be restricted from this screen.
                  </div>
                </>
              )}
            </div>
          )}

          {tab === "subscription" && (
            <div>
              <div className="rounded-2xl border border-primary bg-accent p-5 max-w-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground">
                      Current Plan
                    </p>

                    <p className="font-display text-2xl font-bold capitalize">
                      {rest?.subscription?.plan}
                    </p>
                  </div>

                  <span className="rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-bold uppercase">
                    {rest?.subscription?.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                  <div>
                    <p className="text-muted-foreground text-xs">Billing</p>

                    <p className="font-semibold">
                      {money(rest?.subscription?.amount)}/
                      {rest?.subscription?.cycle}
                    </p>
                  </div>

                  <div>
                    <p className="text-muted-foreground text-xs">Renews</p>

                    <p className="font-semibold">
                      {rest?.subscription?.expiry ||
                      rest?.subscription?.next_billing_date ||
                      rest?.subscription?.trial_end
                        ? new Date(
                            rest.subscription.expiry ||
                              rest.subscription.next_billing_date ||
                              rest.subscription.trial_end,
                          ).toLocaleDateString("en-IN")
                        : "—"}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mt-6 mb-3">
                Available Plans
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {plans.map((p) => {
                  const planKey = String(p.name || "").toLowerCase();

                  const isAvailable =
                    planKey === "starter" || planKey === "basic";

                  const isCurrent =
                    planKey ===
                    String(rest?.subscription?.plan || "").toLowerCase();

                  return (
                    <div
                      key={p.id}
                      className={cn(
                        "rounded-2xl border p-4",
                        isCurrent
                          ? "border-primary bg-accent"
                          : "border-border",
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <p className="font-display font-bold">{p.name}</p>

                        {isCurrent ? (
                          <span className="rounded-full bg-primary/10 text-primary px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap">
                            Selected
                          </span>
                        ) : (
                          <span
                            className={cn(
                              "rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide whitespace-nowrap",
                              isAvailable
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-orange-100 text-orange-700",
                            )}
                          >
                            {isAvailable ? "Available Now" : "Coming Soon"}
                          </span>
                        )}
                      </div>

                      {isAvailable && (
                        <p className="font-display text-xl font-bold mt-4">
                          {money(p.price)}
                          <span className="text-xs text-muted-foreground font-normal">
                            /mo
                          </span>
                        </p>
                      )}

                      {!isAvailable && (
                        <div className="mt-4 text-sm font-semibold text-muted-foreground">
                          Coming Soon
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {tab === "support" && (
            <div className="max-w-3xl space-y-6">
              {/* Header */}
              <div>
                <h3 className="font-display font-semibold text-lg">Support</h3>

                <p className="text-sm text-muted-foreground mt-1">
                  Report a problem and our support team will help you.
                </p>
              </div>

              {/* Create Ticket */}
              <div className="rounded-2xl border border-border p-5">
                <h4 className="font-semibold">Create Support Ticket</h4>

                <div className="mt-5 space-y-4">
                  {/* Category + Priority */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        Category
                      </Label>

                      <select
                        value={supportForm.category}
                        onChange={(e) =>
                          setSupportForm((current) => ({
                            ...current,
                            category: e.target.value,
                          }))
                        }
                        className="mt-1.5 h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                      >
                        <option value="technical_issue">Technical Issue</option>

                        <option value="payment_billing">
                          Payment / Billing
                        </option>

                        <option value="subscription">Subscription</option>

                        <option value="login_account">Login / Account</option>

                        <option value="pos_billing">POS / Billing</option>

                        <option value="menu_inventory">Menu / Inventory</option>

                        <option value="orders_kds">Orders / KDS</option>

                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                        Priority
                      </Label>

                      <select
                        value={supportForm.priority}
                        onChange={(e) =>
                          setSupportForm((current) => ({
                            ...current,
                            priority: e.target.value,
                          }))
                        }
                        className="mt-1.5 h-11 w-full rounded-xl border border-border bg-white px-3 text-sm"
                      >
                        <option value="low">Low</option>

                        <option value="medium">Medium</option>

                        <option value="high">High</option>

                        <option value="urgent">Urgent</option>
                      </select>
                    </div>
                  </div>

                  {/* Subject */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Subject
                    </Label>

                    <Input
                      value={supportForm.subject}
                      onChange={(e) =>
                        setSupportForm((current) => ({
                          ...current,
                          subject: e.target.value,
                        }))
                      }
                      placeholder="What problem are you facing?"
                      className="h-11 rounded-xl mt-1.5"
                      maxLength={200}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
                      Describe the Problem
                    </Label>

                    <textarea
                      value={supportForm.description}
                      onChange={(e) =>
                        setSupportForm((current) => ({
                          ...current,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Please explain the issue in detail..."
                      rows={5}
                      maxLength={5000}
                      className="mt-1.5 w-full rounded-xl border border-border bg-white p-3 text-sm outline-none focus:border-primary"
                    />
                  </div>

                  {/* Submit */}
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      className="h-11 rounded-xl font-semibold"
                      disabled={supportSubmitting}
                      onClick={createSupportTicket}
                    >
                      {supportSubmitting ? "Creating..." : "Create Ticket"}
                    </Button>
                  </div>
                </div>
              </div>

              {/* My Tickets */}
              <div className="rounded-2xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold">My Support Tickets</h4>

                    <p className="text-sm text-muted-foreground mt-1">
                      Track your submitted support requests.
                    </p>
                  </div>

                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 rounded-xl"
                    onClick={loadSupportTickets}
                    disabled={supportLoading}
                  >
                    {supportLoading ? "Loading..." : "Refresh"}
                  </Button>
                </div>

                <div className="mt-5">
                  {supportTickets.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border p-8 text-center">
                      <Headphones
                        size={34}
                        className="mx-auto text-muted-foreground"
                      />

                      <p className="mt-3 font-semibold">No support tickets</p>

                      <p className="text-sm text-muted-foreground mt-1">
                        Your support requests will appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-[520px] overflow-y-auto pr-2 space-y-3 scrollbar-thin scrollbar-thumb-stone-300 scrollbar-track-transparent">
                      {supportTickets.map((ticket) => (
                        <div
                          key={ticket.id}
                          className="rounded-xl border border-border p-4"
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold">
                                  {ticket.ticket_id}
                                </span>

                                <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold">
                                  {CATEGORY_LABELS[ticket.category] || "Other"}
                                </span>

                                <span className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold capitalize">
                                  {ticket.priority}
                                </span>
                              </div>

                              <p className="mt-2 font-semibold">
                                {ticket.subject}
                              </p>

                              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                                {ticket.description}
                              </p>

                              <p className="mt-2 text-xs text-muted-foreground">
                                Created{" "}
                                {ticket.created_at
                                  ? new Date(ticket.created_at).toLocaleString(
                                      "en-IN",
                                    )
                                  : "—"}
                              </p>
                            </div>

                            <span
                              className={cn(
                                "shrink-0 rounded-full px-3 py-1 text-xs font-bold capitalize",
                                ticket.status === "resolved"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : ticket.status === "in_progress"
                                    ? "bg-blue-100 text-blue-700"
                                    : "bg-amber-100 text-amber-700",
                              )}
                            >
                              {ticket.status === "in_progress"
                                ? "In Progress"
                                : ticket.status}
                            </span>
                          </div>

                          {ticket.resolution_note && (
                            <div className="mt-4 rounded-xl bg-emerald-50 border border-emerald-200 p-3">
                              <p className="text-xs font-bold text-emerald-700">
                                Resolution
                              </p>

                              <p className="mt-1 text-sm">
                                {ticket.resolution_note}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Field = ({
  label,
  value,
  onSave,
  onChange,
  testid,
  error,
  required = false,
}) => {
  const [v, setV] = useState(value || "");

  useEffect(() => {
    setV(value || "");
  }, [value]);

  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {label}
        {required && <span className="text-destructive ml-1">*</span>}
      </Label>

      <div className="flex gap-2 mt-1.5">
        <Input
          value={v}
          onChange={(e) =>
            onChange ? onChange(e.target.value) : setV(e.target.value)
          }
          className={cn(
            "h-11 rounded-xl",
            error && "border-destructive focus-visible:ring-destructive",
          )}
          data-testid={testid}
          readOnly={!onSave && !onChange}
        />

        {onSave && (
          <Button
            onClick={() => onSave(v)}
            variant="outline"
            className="h-11 rounded-xl"
          >
            Save
          </Button>
        )}
      </div>

      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
  );
};

const NumField = ({ label, value, onSave, testid }) => {
  const [v, setV] = useState(value ?? 0);
  useEffect(() => setV(value ?? 0), [value]);
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </Label>
      <div className="flex gap-2 mt-1.5">
        <Input
          type="number"
          value={v}
          onChange={(e) => setV(e.target.value)}
          className="h-11 rounded-xl"
          data-testid={testid}
        />
        <Button
          onClick={() => onSave(v)}
          variant="outline"
          className="h-11 rounded-xl"
        >
          Save
        </Button>
      </div>
    </div>
  );
};

// Write-only secret field — never shows existing value, only lets you update it
const SecretField = ({ label, placeholder, onSave, testid, helpText }) => {
  const [v, setV] = useState("");
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
        {label}
      </Label>
      <div className="flex gap-2 mt-1.5">
        <Input
          type="password"
          value={v}
          onChange={(e) => setV(e.target.value)}
          placeholder={placeholder}
          className="h-11 rounded-xl"
          data-testid={testid}
          autoComplete="new-password"
        />
        <Button
          onClick={() => {
            if (v) {
              onSave(v);
              setV("");
            }
          }}
          variant="outline"
          className="h-11 rounded-xl"
          disabled={!v}
        >
          Save
        </Button>
      </div>
      {helpText && (
        <p className="text-xs text-muted-foreground mt-1">{helpText}</p>
      )}
    </div>
  );
};
